import React, { useState, useEffect } from 'react';
import {
  User,
  CheckCircle2,
  Search,
  Activity,
  Move,
} from 'lucide-react';
import { ActivityCard } from './ActivityCard';
import { extractTargetName } from 'utils';

const UserActivity = ({ data }) => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    data && setActivities(data.map(e => ({ ...e, target: extractTargetName(e.description) })))
  }, [data])

  const filterTypes = [
    { id: 'all', label: 'Tất cả', count: activities.length },
    { id: 'task', label: 'Tasks', count: activities.filter(a => a.type === 'task').length },
    { id: 'project', label: 'Projects', count: activities.filter(a => a.type === 'project').length },
    { id: 'board', label: 'Boards', count: activities.filter(a => a.type === 'board').length }
  ];

  const actionTypes = [
    { id: 'created', label: 'Tạo mới', count: activities.filter(a => a.action === 'created').length },
    { id: 'updated', label: 'Cập nhật', count: activities.filter(a => a.action === 'updated').length },
    { id: 'moved', label: 'Di chuyển', count: activities.filter(a => a.action === 'moved').length },
    { id: 'deleted', label: 'Xóa', count: activities.filter(a => a.action === 'deleted').length }
  ];

  const uniqueUsers = [...new Set(activities.map(a => a.user.name))];

  const filteredActivities = activities.filter(activity => {
    const matchesFilter = selectedFilter === 'all' || activity.type === selectedFilter || activity.action === selectedFilter;
    const matchesUser = selectedUser === 'all' || activity.user.name === selectedUser;
    const matchesSearch = activity.target.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.project.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesUser && matchesSearch;
  });

  return (
    <div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Tổng hoạt động</p>
              <p className="text-2xl font-bold text-gray-900">{activities.length}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-500 text-white">
              <Activity className="w-5 h-5" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Thành viên tham gia</p>
              <p className="text-2xl font-bold text-gray-900">{uniqueUsers.length}</p>
            </div>
            <div className="p-3 rounded-lg bg-green-500 text-white">
              <User className="w-5 h-5" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Tasks được tạo</p>
              <p className="text-2xl font-bold text-gray-900">
                {activities.filter(a => a.action === 'created' && a.type === 'task').length}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-purple-500 text-white">
              <CheckCircle2 className="w-5 h-5" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Di chuyển Tasks</p>
              <p className="text-2xl font-bold text-gray-900">
                {activities.filter(a => a.action === 'moved').length}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-red-500 text-white">
              <Move className="w-5 h-5" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Filter tabs */}
          <div className="flex flex-wrap gap-2">
            {filterTypes.map(filter => (
              <button
                key={filter.id}
                onClick={() => setSelectedFilter(filter.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedFilter === filter.id
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                {filter.label} ({filter.count})
              </button>
            ))}
          </div>

          {/* Action filters */}
          <div className="flex flex-wrap gap-2">
            {actionTypes.map(action => (
              <button
                key={action.id}
                onClick={() => setSelectedFilter(action.id)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${selectedFilter === action.id
                  ? 'bg-purple-100 text-purple-700 border border-purple-200'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
              >
                {action.label} ({action.count})
              </button>
            ))}
          </div>

          <div className="flex gap-3 lg:ml-auto">
            {/* User filter */}
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tất cả thành viên</option>
              {uniqueUsers.map(user => (
                <option key={user} value={user}>{user}</option>
              ))}
            </select>

            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Tìm kiếm hoạt động..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Hoạt động</h2>

        <div className="space-y-4">
          {filteredActivities.map((activity, index) => (
            <div key={activity.id} className="relative">
              {/* {index !== filteredActivities.length - 1 && (
                <div className="absolute left-5 top-12 w-0.5 h-full bg-gray-200"></div>
              )} */}
              <ActivityCard activity={activity} />
            </div>
          ))}
        </div>

        {filteredActivities.length === 0 && (
          <div className="text-center py-8">
            <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Không có hoạt động nào phù hợp với bộ lọc</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserActivity;